import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import {
  distinct,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Location } from '@angular/common';

import { State as ContactsState } from 'src/app/core/services/contacts/contacts.reducer';
import {
  selectGroupMembers,
  selectGroupRequests,
  State as GroupState
} from 'src/app/core/services/group/group.reducer';
import {
  selectEvents,
  State as EventState
} from 'src/app/core/services/events/events.reducer';
import {
  removeGroup,
  updateGroupInfo,
  loadGroupMembers,
  subscribeGroup,
  unsubscribeGroup,
  loadGroupRequests,
  accessGroup,
  unaccessGroup
} from 'src/app/core/services/group/group.actions';
import { CreateSchoolClubChapterDialogComponent } from 'src/app/shared/components/create-school-club-chapter-dialog/create-school-club-chapter-dialog.component';
import { InviteUserDialogComponent } from 'src/app/shared/components/dialogs';
import { ClubFacade } from 'src/app/core/services/clubs/club.facade';
import {
  IMessage,
  IMessageCtx
} from 'src/app/shared/components/message/message.component';
import { PostState } from 'src/app/shared/ui/post/data/model/state';
import { loadPostsByGroupId } from 'src/app/shared/ui/post/data/store/actions';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  selectPosts,
  selectStatus
} from 'src/app/shared/ui/post/data/store/selector';
import { IPost } from 'src/app/shared/models/post';
import { IPostsCtx } from 'src/app/shared/components/posts/list/posts.component';
import { User, Request } from 'src/app/core/services/group/group.model';
import { AuthService } from 'src/app/core/services/auth.service';
import {
  addContact,
  removeContact
} from 'src/app/core/services/contacts/contacts.actions';
import { TStatus } from 'src/app/shared/models/status';
import { loadEvents } from 'src/app/core/services/events/events.actions';
import { Event } from 'src/app/core/services/events/event.model';
import { Club } from 'src/app/core/services/clubs/club.model';
import { ImagesService } from 'src/app/shared/stores/images/images.service';
import { DocsService } from 'src/app/shared/stores/docs/docs.service';
import { IPostCtx } from 'src/app/shared/components/posts/item/post.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SavedPostsFacade } from 'src/app/shared/stores/posts/saved/posts.saved.facade';
import {
  State as DialogsState,
  selectDialogs
} from 'src/app/core/services/dialogs/dialogs.reducer';
import { NewMessageDialogComponent } from 'src/app/shared/components/new-message-dialog/new-message-dialog.component';
import { unrequestGroup } from 'src/app/core/services/groups/groups.actions';
import { SocketService } from 'src/app/core/services/socket.service';

@Component({
  selector: 'app-club-container',
  templateUrl: './club-container.component.html',
  styleUrls: ['./club-container.component.scss']
})
export class ClubContainerComponent implements OnInit, OnDestroy {
  sections = ClubContainerComponent.Section;

  @Input() set club(value: Club) {
    this._club = value;
    this.clubFacade.selectClub(value);
  }

  get club(): Club {
    return this._club;
  }

  @Input() messageCtx: IMessageCtx;
  @Input() postsCtx: IPostsCtx;

  posts$: Observable<IPost[]> = null;
  postsStatus$: Observable<TStatus> = null;
  members$: Observable<{
    members: User[];
    loadMembers: boolean;
    count: number;
  }> = null;
  requests$: Observable<{
    requests: Request[];
    loadRequests: boolean;
    count: number;
  }> = null;
  events$: Observable<{ items: Event[]; loadEvents: boolean; count: number }> =
    null;
  section$ = new BehaviorSubject<ClubContainerComponent.Section>(null);
  page$ = new BehaviorSubject<number>(null);
  search$ = new BehaviorSubject<string>('');
  selectedMediaType$: BehaviorSubject<string> = new BehaviorSubject<string>(
    ClubContainerComponent.MediaTypes.Photos
  );
  mediaTypes = ClubContainerComponent.MediaTypes;
  mediaPage$ = new BehaviorSubject(1);

  private _componentDestroy$ = new Subject();
  private _club = null;

  get isAuthorized() {
    return this.auth.isFullyAuthorized;
  }

  get mediaPhotos$() {
    return this.clubFacade.photos$;
  }

  get mediaFiles$() {
    return this.clubFacade.files$;
  }

  get resources$() {
    return this.clubFacade.resources$;
  }

  get canMakePosts() {
    return this.club?.access_posted;
  }

  get canMakeResource() {
    return this.club?.can_post_resource;
  }

  readonly resourcesMessageCtx: IMessageCtx = {
    placeholder: `Post an update, article or question`,
    submit: (message) => this.onResourceMessageSubmit(message),
    isSupportTags: true,
    isSupportLinkPreview: true
  };

  readonly resourcesPostCtx: IPostsCtx = {
    status: this.clubFacade.resourcesStatus$,
    delete: this.onDeleteResource.bind(this),
    like: this.onLikeResource.bind(this),
    complain: this.onComplainToResource.bind(this),
    share: ({ id }, message) => {},
    update: this.onUpdateResource.bind(this),
    save: this.onSaveResource.bind(this),
    paginate: this.onLoadResourcesForPage.bind(this),
    modifyers: {
      isGroup: true,
      isForbidComments: of(true)
    },
    comments: null
  };

  constructor(
    private readonly snackbar: MatSnackBar,
    private readonly router: Router,
    private readonly location: Location,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dialog: MatDialog,
    // TODO: move all groupStore logic to clubFacade
    private readonly groupStore: Store<GroupState>,
    private readonly contactsStore: Store<ContactsState>,
    private readonly eventStore: Store<EventState>,
    private readonly dialogsStore: Store<DialogsState>,
    private readonly clubFacade: ClubFacade,
    private readonly savedPostsFacade: SavedPostsFacade,
    private readonly postStore: Store<PostState>,
    private readonly auth: AuthService,
    private readonly imagesService: ImagesService,
    private readonly docsService: DocsService,
    private readonly snackBar: MatSnackBar,
    private readonly _socketService: SocketService
  ) {
    this.posts$ = this.postStore.pipe(select(selectPosts({})));
    this.postsStatus$ = this.postStore.pipe(select(selectStatus()));
    this.members$ = this.groupStore.pipe(select(selectGroupMembers()));
    this.requests$ = this.groupStore.pipe(select(selectGroupRequests()));
    this.events$ = this.groupStore.pipe(select(selectEvents()));
  }

  ngOnInit() {
    this.listenParams();
    this.listenSectionChange();
    this.listenPageChange();
    this.listenSearchChange();
    this.listenMediaTypeChange();
  }

  ngOnDestroy() {
    this.clubFacade.unselectClub();

    this._componentDestroy$.next();
    this._componentDestroy$.complete();
  }

  onLeave() {
    this.groupStore.dispatch(
      unsubscribeGroup({ group_id: this.club.id, user_id: this.auth.userID() })
    );
  }

  onCallOff() {
    this.groupStore.dispatch(
      unrequestGroup({ group_id: this.club.id, user_id: this.auth.userID() })
    );
  }

  onJoin() {
    this.openMessageDialog(
      'Join club',
      'Why are you interested in joining our club?'
    )
      .pipe(
        takeUntil(this._componentDestroy$),
        take(1),
        filter((_) => _ && _.isSend),
        tap((_) =>
          this.groupStore.dispatch(
            subscribeGroup({
              group_id: this.club.id,
              user_id: this.auth.userID(),
              text: _.message
            })
          )
        ),
        tap((_) => this.messageHasBeenSent())
      )
      .subscribe((_) => _);
  }

  onProfileConnect(id: number) {
    this.contactsStore.dispatch(addContact({ id }));
  }

  onProfileDisconnect(id: number) {
    this.contactsStore.dispatch(removeContact({ id }));
  }

  onAcceptRequest(user: User) {
    this.groupStore.dispatch(accessGroup({ group_id: this.club.id, user }));
  }

  onRejectRequest(user: User) {
    this.groupStore.dispatch(unaccessGroup({ group_id: this.club.id, user }));
  }

  onEdit() {
    const dialogRef = this.dialog.open(CreateSchoolClubChapterDialogComponent, {
      data: { groupInfo: this.club }
    });

    const afterClosed = dialogRef.afterClosed();

    const submit$ = afterClosed.pipe(
      take(1),
      filter((res) => res && res.type === 'submit'),
      tap((res) =>
        this.groupStore.dispatch(
          updateGroupInfo({ id: res.id, group: res.payload })
        )
      )
    );

    const delete$ = afterClosed.pipe(
      take(1),
      filter((res) => res && res.type === 'remove'),
      tap((res) => this.groupStore.dispatch(removeGroup({ id: res.id })))
    );

    combineLatest([submit$, delete$])
      .pipe(take(1))
      .subscribe((_) => _);
  }

  async onOpenMessageDialog() {
    this.openMessageDialog(
      'Contact club',
      'If you would like to contact this club include your message here. Introduce yourself or your club. Clubs and chapters may be open for partnership or other joint opportunities with other students, clubs or organizations.'
    )
      .pipe(
        takeUntil(this._componentDestroy$),
        take(1),
        filter((_) => _ && _.isSend && _.message),
        tap(({ contact, message }): any => this.sendMessage(contact, message))
      )
      .subscribe((_) => _);
  }

  onOpenInviteDialog() {
    const dialogRef = this.dialog.open(InviteUserDialogComponent);

    dialogRef
      .afterClosed()
      .pipe(
        first(),
        filter((_) => !!_),
        mergeMap((_) => this.sendInvite(_)),
        tap((_) =>
          this.snackbar.open('Invitation has been sent.', 'OK', {
            duration: 3000
          })
        )
      )
      .subscribe((_) => _);
  }

  tabChanged($event: MatTabChangeEvent) {
    console.log('tabChanged: ', $event, $event.tab.textLabel);

    this.section$.next(this.parseSectionParam($event.tab.textLabel));
    this.search$.next(null);
  }

  mapSectionToIndex(section: ClubContainerComponent.Section) {
    return this.sectionToIndex(section);
  }

  onSetPage(page: number) {
    if (!Number.isSafeInteger(Number(page))) {
      return;
    }

    this.page$.next(page);
  }

  onChangeMediaFilterType(type: string) {
    this.selectedMediaType$.next(type);
  }

  onSelectPhoto(file: File) {
    this.imagesService
      .add([file])
      .pipe(
        first(),
        map((_) => this.clubFacade.attachPhotos(this.club.id, _))
      )
      .subscribe((_) => _);
  }

  onSelectFile(file: File) {
    this.docsService
      .add([file])
      .pipe(
        first(),
        map((_) => this.clubFacade.attachFiles(this.club.id, _))
      )
      .subscribe((_) => _);
  }

  onRemovePhoto(id: number) {
    if (!this.club?.id) {
      return;
    }

    this.clubFacade.removePhoto(this.club.id, id);
  }

  onRemoveFile(id: number) {
    if (!this.club?.id) {
      return;
    }

    this.clubFacade.removeFile(this.club.id, id);
  }

  onLoadMorePhotos() {
    this.mediaPage$.next(this.mediaPage$.value + 1);
    this.clubFacade.loadMediaPhotos(this.club.id, this.mediaPage$.value);
  }

  onResourceMessageSubmit(message: IMessage) {
    if (!this.club?.id) {
      return;
    }

    this.clubFacade.postResource(this.club.id, message);
  }

  onSearch(term: string) {
    this.search$.next(term);
  }

  private loadPosts() {
    console.log('load posts');

    if (!this.club?.id) {
      return;
    }

    this.postStore.dispatch(loadPostsByGroupId({ id: this.club.id }));
  }

  private loadMembers() {
    if (!this.club?.id || !this.auth.isFullyAuthorized) {
      return;
    }

    this.groupStore.dispatch(
      loadGroupMembers({
        id: this.club.id,
        page: this.page$.value,
        searchTerm: this.search$.value || ''
      })
    );
  }

  private loadRequests() {
    if (!this.club?.id) {
      return;
    }

    console.log('loadRequests');

    this.groupStore.dispatch(
      loadGroupRequests({
        id: this.club.id,
        page: this.page$.value,
        searchTerm: this.search$.value || ''
      })
    );
  }

  private loadEvents() {
    console.log('load events');

    if (!this.club?.id) {
      return;
    }

    this.eventStore.dispatch(
      loadEvents({ id: this.club.id, page: this.page$.value })
    );
  }

  private loadMedia() {
    if (!this.club?.id) {
      return;
    }

    switch (this.selectedMediaType$.value) {
      case ClubContainerComponent.MediaTypes.Files:
        return this.clubFacade.loadMediaFiles(this.club.id, this.page$.value);
      default:
        return this.clubFacade.loadMediaPhotos(
          this.club.id,
          this.mediaPage$.value
        );
    }
  }

  private loadResources() {
    console.log('load resources');

    if (!this.club?.id) {
      return;
    }

    this.clubFacade.loadResources(this.club.id, this.page$.value);
  }

  private onLikeResource(resource: IPost) {
    if (!this.club.id) {
      return;
    }

    this.clubFacade.likeResource(this.club.id, resource.id);
  }

  private onUpdateResource(resource: IPost, message: IMessage) {
    if (!this.club.id) {
      return;
    }

    this.clubFacade.updateResource(this.club.id, resource.id, {
      description: message.text,
      foto: message.images,
      file: message.docs
    });
  }

  private onComplainToResource(resource: IPost) {
    if (!this.club.id) {
      return;
    }

    this.clubFacade
      .complainToResource(resource.id)
      .pipe(
        takeUntil(this._componentDestroy$),
        tap((_) =>
          this.snackbar.open('Your complain was accepted! Thank you.', 'OK', {
            duration: 3000
          })
        )
      )
      .subscribe((_) => _);
  }

  private onDeleteResource(resource: IPost) {
    if (!this.club.id) {
      return;
    }

    this.clubFacade.deleteResource(this.club.id, resource.id);
  }

  private onSaveResource(resource: IPost) {
    if (resource.is_saved) {
      return this.clubFacade.unsaveResource(this.club.id, resource.id);
    }

    return this.clubFacade.saveResource(this.club.id, resource.id);
  }

  private onLoadResourcesForPage(_, page: number) {
    if (!this.club.id) {
      return;
    }

    this.clubFacade.loadResources(this.club.id, page);
  }

  private listenParams() {
    this.activatedRoute.params
      .pipe(
        takeUntil(this._componentDestroy$),
        tap((_) => {
          const section = this.parseSectionParam(_.section);
          this.section$.next(section);
        }),
        tap((_) => {
          const page = Number.isSafeInteger(Number(_.page))
            ? Number(_.page)
            : 1;
          this.page$.next(page);
        }),
        tap((_) => {
          console.log('media type: ', _.mediaType);

          _.mediaType &&
            this.selectedMediaType$.next(this.parseMediaType(_.mediaType));
        })
      )
      .subscribe((_) => _);

    this.activatedRoute.queryParams
      .pipe(
        takeUntil(this._componentDestroy$),
        tap((_) => {
          const search = _.search || '';
          this.search$.next(search);
        })
      )
      .subscribe((_) => _);
  }

  private listenSectionChange() {
    this.section$
      .pipe(
        takeUntil(this._componentDestroy$),
        tap((_) => this.page$.next(this.getInitialPageForSection(_))),
        tap((_) => this.onNewSection(_)),
        tap((_) => this.updateLocation())
      )
      .subscribe((_) => _);
  }

  private listenPageChange() {
    this.page$
      .pipe(
        takeUntil(this._componentDestroy$),
        filter((_) => !!_),
        tap((_) => this.updateLocation()),
        // TODO: load twice during first page load
        tap((_) => this.onNewSection(this.section$.value))
      )
      .subscribe((_) => _);
  }

  private listenSearchChange() {
    this.search$
      .pipe(
        takeUntil(this._componentDestroy$),
        tap((_) => this.updateLocation()),
        // TODO: load twice during first page load
        tap((_) => this.onNewSection(this.section$.value))
      )
      .subscribe((_) => _);
  }

  private listenMediaTypeChange() {
    combineLatest([this.selectedMediaType$, this.section$])
      .pipe(
        takeUntil(this._componentDestroy$),
        map((_) => ({ mediaType: _[0], section: _[1] })),
        filter((_) => !!_.mediaType),
        filter((_) => _.section === ClubContainerComponent.Section.Media),
        tap((_) => this.updateLocation()),
        tap((_) => this.loadMedia())
      )
      .subscribe((_) => _);
  }

  private onNewSection(section: ClubContainerComponent.Section) {
    switch (section) {
      case ClubContainerComponent.Section.Members:
        return this.loadMembers();
      case ClubContainerComponent.Section.Requests:
        return this.loadRequests();
      case ClubContainerComponent.Section.Events:
        return this.loadEvents();
      case ClubContainerComponent.Section.Media:
        return this.loadMedia();
      case ClubContainerComponent.Section.Resources:
        return this.loadResources();
      default:
        return this.loadPosts();
    }
  }

  private parseSectionParam(section: string) {
    const isAuth = this.auth.isFullyAuthorized;

    switch (section) {
      case 'updates':
        return ClubContainerComponent.Section.Updates;
      case 'members':
        return isAuth
          ? ClubContainerComponent.Section.Members
          : ClubContainerComponent.Section.Updates;
      case 'requests':
        return isAuth
          ? ClubContainerComponent.Section.Requests
          : ClubContainerComponent.Section.Updates;
      // case 'events':
      //   return ClubContainerComponent.Section.Events;
      case 'media':
        return ClubContainerComponent.Section.Media;
      case 'resources':
        return ClubContainerComponent.Section.Resources;
      default:
        return ClubContainerComponent.Section.Updates;
    }
  }

  private parseMediaType(mediaType: string) {
    switch (mediaType) {
      case 'photos':
        return ClubContainerComponent.MediaTypes.Photos;
      case 'videos':
        return ClubContainerComponent.MediaTypes.Videos;
      case 'files':
        return ClubContainerComponent.MediaTypes.Files;
      default:
        return ClubContainerComponent.MediaTypes.Photos;
    }
  }

  private sectionToIndex(section: ClubContainerComponent.Section) {
    switch (section) {
      case ClubContainerComponent.Section.Updates:
        return 0;
      case ClubContainerComponent.Section.Members:
        return 1;
      // case ClubContainerComponent.Section.Events:
      //   return 2;
      case ClubContainerComponent.Section.Resources:
        return 2;
      case ClubContainerComponent.Section.Media:
        return 3;
      case ClubContainerComponent.Section.Requests:
        return 4;
      default:
        return 0;
    }
  }

  // TODO:
  // clean up after tab index fix; (how to hide members properly)
  // private indexToSection(index: number) {
  //   switch (index) {
  //     case 0:
  //       return ClubContainerComponent.Section.Updates;
  //     case 1:
  //       return ClubContainerComponent.Section.Members;
  //     // case 2:
  //     //   return ClubContainerComponent.Section.Events;
  //     case 2:
  //       return ClubContainerComponent.Section.Resources;
  //     case 3:
  //       return ClubContainerComponent.Section.Media;
  //     default:
  //       return ClubContainerComponent.Section.Updates;
  //   }
  // }

  private getInitialPageForSection(section: ClubContainerComponent.Section) {
    switch (section) {
      case ClubContainerComponent.Section.Members:
      case ClubContainerComponent.Section.Requests:
        return this.page$.value || 1;
      default:
        return null;
    }
  }

  private sendInvite(email: string) {
    return this.clubFacade.sendInvitation(this.club.id, email);
  }

  private updateLocation() {
    this.activatedRoute.parent.url
      .pipe(
        takeUntil(this._componentDestroy$),
        first(),
        map((_) => _[_.length - 1].path),
        tap((_) => {
          this.location.replaceState(
            this.router
              .createUrlTree(
                [
                  _,
                  this.activatedRoute.snapshot.params.id,
                  this.activatedRoute.snapshot.params.name,
                  this.section$.value ? this.section$.value : undefined,
                  this.section$.value === ClubContainerComponent.Section.Media
                    ? this.selectedMediaType$.value
                    : undefined,
                  this.page$.value ? this.page$.value : undefined
                ].filter((_) => _),
                {
                  queryParams: {
                    search: this.search$.value ? this.search$.value : undefined
                  }
                }
              )
              .toString()
          );
        })
      )
      .subscribe((_) => _);
  }

  private openMessageDialog(customTitle?: string, customPlaceholder?: string) {
    const receiverID = this.club.user?.dialog_id || null;

    return this.dialog
      .open(NewMessageDialogComponent, {
        data: {
          contact: this.club.user,
          receiverID: receiverID,
          customTitle,
          customPlaceholder
        }
      })
      .afterClosed();
  }

  private sendMessage(userID: number, text: string) {
    this._socketService.sendMessage(userID, text, null, null);
    this.messageHasBeenSent();
  }

  private messageHasBeenSent() {
    this.snackBar.open('Message has been sent!', 'OK', {
      duration: 10 * 1000
    });
  }
}

export namespace ClubContainerComponent {
  export enum Section {
    Updates = 'updates',
    Members = 'members',
    Requests = 'requests',
    Events = 'events',
    Media = 'media',
    Resources = 'resources'
  }

  export enum MediaTypes {
    Photos = 'photos',
    Videos = 'videos',
    Files = 'files'
  }
}
